import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, Tag, Share2, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import LazyImage from '../components/LazyImage';
import NewsletterCapture from '../components/NewsletterCapture';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/blog/${slug}`)
      .then(res => { if (!res.ok) throw new Error(); return res.json(); })
      .then(data => setPost(data))
      .catch(() => navigate('/blog'))
      .finally(() => setLoading(false));
  }, [slug, navigate]);

  const formatDate = (d) => new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
  const readTime = (content) => `${Math.max(1, Math.ceil(content.replace(/<[^>]*>/g, '').split(/\s+/).length / 200))} min read`;

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({ title: post.title, url: window.location.href });
    } else {
      navigator.clipboard.writeText(window.location.href);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-[#00AEEF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!post) return null;

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Cover */}
      <div className="relative h-[50vh] min-h-[350px] max-h-[500px]">
        <img src={post.cover_image} alt={post.title} loading="eager" fetchPriority="high" decoding="async" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12 max-w-4xl mx-auto">
          <Link to="/blog" className="inline-flex items-center gap-1.5 text-white/70 hover:text-white text-sm mb-4 transition-colors" data-testid="blog-back-link">
            <ArrowLeft className="w-4 h-4" /> Back to Blog
          </Link>
          <div className="flex items-center gap-3 mb-3 text-sm">
            <span className="bg-[#00AEEF] text-white px-3 py-1 rounded-full font-medium">{post.category}</span>
            <span className="text-white/70 flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{formatDate(post.created_at)}</span>
            <span className="text-white/70 flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{readTime(post.content)}</span>
          </div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white leading-tight" data-testid="blog-post-title">
            {post.title}
          </h1>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-10">
        <div className="flex items-center justify-between mb-8 pb-6 border-b">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#003366] rounded-full flex items-center justify-center">
              <User className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-semibold text-[#003366] text-sm">{post.author}</p>
              <p className="text-xs text-gray-400">Pool Construction Experts</p>
            </div>
          </div>
          <button onClick={handleShare} className="flex items-center gap-1.5 text-gray-400 hover:text-[#00AEEF] transition-colors text-sm" data-testid="blog-share-btn">
            <Share2 className="w-4 h-4" /> Share
          </button>
        </div>

        {/* Article body */}
        <article
          className="prose prose-lg max-w-none
            prose-headings:text-[#003366] prose-headings:font-bold
            prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
            prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
            prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-4
            prose-li:text-gray-700 prose-li:leading-relaxed
            prose-strong:text-[#003366]
            prose-a:text-[#00AEEF] prose-a:no-underline hover:prose-a:underline"
          data-testid="blog-post-content"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />

        {/* Image Gallery */}
        {post.images && post.images.length > 1 && (
          <div className="mt-12">
            <h3 className="text-xl font-bold text-[#003366] mb-6">Project Gallery</h3>
            <div className="grid grid-cols-2 gap-4">
              {post.images.map((img, i) => (
                <LazyImage
                  key={i}
                  src={img}
                  alt={`${post.title} - Image ${i + 1}`}
                  className="w-full h-56 md:h-72 rounded-xl shadow-md"
                />
              ))}
            </div>
          </div>
        )}

        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <div className="mt-10 pt-6 border-t">
            <div className="flex flex-wrap gap-2">
              {post.tags.map(tag => (
                <span key={tag} className="flex items-center gap-1 text-sm text-gray-500 bg-gray-100 px-3 py-1.5 rounded-full">
                  <Tag className="w-3 h-3" />{tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Newsletter Capture */}
        <NewsletterCapture source={`blog:${post.slug}`} />

        {/* CTA */}
        <div className="mt-12 bg-gradient-to-r from-[#003366] to-[#001a33] rounded-2xl p-8 md:p-10 text-white text-center">
          <h2 className="text-2xl font-bold mb-3">Inspired by This Project?</h2>
          <p className="text-[#00AEEF]/80 mb-6 max-w-lg mx-auto">Let Aquarist Pools bring your dream pool to life. Get a free consultation and custom quote today.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button asChild className="bg-[#00AEEF] hover:bg-[#0098d4] text-white px-8 py-6 text-base font-semibold">
              <Link to="/booking">Get a Free Quote</Link>
            </Button>
            <Button asChild variant="outline" className="border-2 border-white/30 text-white hover:bg-white/10 px-8 py-6 text-base font-semibold">
              <Link to="/contact">Contact Us</Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPost;
